import React, { useState } from "react";
import { toast } from "react-toastify";
import LoadingSpinner from "./LoadingSpinner";
import { createUser } from "../services/adminService";
import { COUNTRIES } from "../constants/countries";

const CreateUserModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    businessName: "",
    email: "",
    phone: "",
    accessToken: "",
    phoneNumberId: "",
    wabaId: "",
    appId: "",
    role: "user",
    country: "",
    countryCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Email is always required
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email format is invalid";
    }

    // For regular users, all WhatsApp fields are required
    if (formData.role === "user") {
      // Required fields validation for regular users
      if (!formData.businessName.trim()) {
        newErrors.businessName = "Business name is required";
      }

      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (formData.country) {
        const selectedCountry = COUNTRIES.find((c) => c.name === formData.country);
        if (selectedCountry) {
          const digits = formData.phone.replace(/\D/g, "").length;
          if (!selectedCountry.digits.includes(digits)) {
            const expected = selectedCountry.digits.join(" or ");
            newErrors.phone = `Phone number must be ${expected} digits for ${formData.country}`;
          }
        }
      }

      if (!formData.accessToken.trim()) {
        newErrors.accessToken = "Access token is required";
      }

      if (!formData.phoneNumberId.trim()) {
        newErrors.phoneNumberId = "Phone number ID is required";
      }

      if (!formData.wabaId.trim()) {
        newErrors.wabaId = "WABA ID is required";
      }

      if (!formData.appId.trim()) {
        newErrors.appId = "App ID is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCountryChange = (e) => {
    const selected = COUNTRIES.find((c) => c.name === e.target.value);
    setFormData((prev) => ({
      ...prev,
      country: selected ? selected.name : "",
      countryCode: selected ? selected.code : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      await createUser(formData);

      toast.success(
        "User created successfully! Login credentials have been sent to their email."
      );

      // Reset form
      setFormData({
        businessName: "",
        email: "",
        phone: "",
        accessToken: "",
        phoneNumberId: "",
        wabaId: "",
        appId: "",
        role: "user",
        country: "",
        countryCode: "",
      });
      setErrors({});

      // Call success callback and close modal
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error.response?.data?.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        businessName: "",
        email: "",
        phone: "",
        accessToken: "",
        phoneNumberId: "",
        wabaId: "",
        appId: "",
        role: "user",
        country: "",
        countryCode: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">Add New User</h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition duration-200"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Role Selection - Moved to top */}
          <div>
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              User Role *
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <p className="text-gray-500 text-xs mt-1">
              {formData.role === "admin"
                ? "Admin users don't require WhatsApp configuration"
                : "Regular users require complete WhatsApp configuration"}
            </p>
          </div>

          {/* Business Name */}
          <div>
            <label
              htmlFor="businessName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business Name {formData.role === "user" ? "*" : ""}
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.businessName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter business name"
            />
            {errors.businessName && (
              <p className="text-red-500 text-sm mt-1">{errors.businessName}</p>
            )}
          </div>

          {/* Country */}
          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Country
            </label>
            <select
              id="country"
              value={formData.country}
              onChange={handleCountryChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select country</option>
              {COUNTRIES.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>

          {/* Email and Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number {formData.role === "user" ? "*" : ""}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                maxLength={
                  formData.country
                    ? Math.max(...(COUNTRIES.find((c) => c.name === formData.country)?.digits || [20]))
                    : undefined
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                }`}
                placeholder={
                  formData.country
                    ? `Enter ${(COUNTRIES.find((c) => c.name === formData.country)?.digits || []).join("/")} digit number`
                    : "Enter phone number"
                }
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
              {formData.country && !errors.phone && (
                <p className="text-gray-500 text-xs mt-1">
                  {(() => {
                    const c = COUNTRIES.find((x) => x.name === formData.country);
                    return c ? `${formData.country} requires ${c.digits.join(" or ")} digit phone number` : "";
                  })()}
                </p>
              )}
            </div>
          </div>

          {/* WhatsApp Configuration */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 border-t pt-4">
              WhatsApp Configuration{" "}
              {formData.role === "user" ? "*" : "(Optional)"}
            </h4>

            <div>
              <label
                htmlFor="accessToken"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Access Token {formData.role === "user" ? "*" : ""}
              </label>
              <input
                type="text"
                id="accessToken"
                name="accessToken"
                value={formData.accessToken}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.accessToken ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter WhatsApp access token"
              />
              {errors.accessToken && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.accessToken}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="phoneNumberId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number ID {formData.role === "user" ? "*" : ""}
              </label>
              <input
                type="text"
                id="phoneNumberId"
                name="phoneNumberId"
                value={formData.phoneNumberId}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.phoneNumberId ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter phone number ID"
              />
              {errors.phoneNumberId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.phoneNumberId}
                </p>
              )}
            </div>

            {/* WABA ID and App ID */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="wabaId"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  WhatsApp Business Account ID (WABA ID){" "}
                  {formData.role === "user" ? "*" : ""}
                </label>
                <input
                  type="text"
                  id="wabaId"
                  name="wabaId"
                  value={formData.wabaId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.wabaId ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter WABA ID"
                />
                {errors.wabaId && (
                  <p className="text-red-500 text-sm mt-1">{errors.wabaId}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Required for template management
                </p>
              </div>

              <div>
                <label
                  htmlFor="appId"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Facebook App ID {formData.role === "user" ? "*" : ""}
                </label>
                <input
                  type="text"
                  id="appId"
                  name="appId"
                  value={formData.appId}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.appId ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter App ID"
                />
                {errors.appId && (
                  <p className="text-red-500 text-sm mt-1">{errors.appId}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Required for media uploads
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Creating...</span>
              </>
            ) : (
              "Create User"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
